import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Stack, useTheme } from "@bps/fluent-ui";
import { UseFormMethods } from "@bps/form-api/dist/src/lib/form-api.types";
import { HttpError } from "@bps/http-client";
import { SectionTitle } from "@components/SectionTitle";
import { Selector } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useCreateSelector,
  useUpdateSelector
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { SelectorFormValues } from "./formUtils";
import { SelectorForm } from "./SelectorForm";

interface EditSelectorHostProps {
  selector?: Selector;
  modeLabel: "Edit" | "New";
}

export const EditSelectorHost: React.FC<EditSelectorHostProps> = ({
  selector,
  modeLabel
}) => {
  const theme = useTheme();

  const { mutateAsync: updateSelector } = useUpdateSelector();
  const { mutateAsync: createSelector } = useCreateSelector();

  const navigate = useNavigate();

  const [error, setError] = useState<HttpError | null>();

  const onDismiss = () => navigate("/system/plt/selectors");

  const onSubmit = async (
    values: SelectorFormValues,
    actions: Pick<UseFormMethods<SelectorFormValues>, "reset" | "setValue">
  ) => {
    const { rules, ...rest } = values;

    setError(undefined);

    try {
      if (!selector) {
        await createSelector({
          ...rest,
          rules: rules.map(rule => {
            // Drop frontend id from rule.
            return {
              attributeType: rule.attributeType,
              comparisonOperator: rule.comparisonOperator,
              value: rule.value,
              valueDataType: rule.valueDataType
            };
          })
        });
      } else {
        await updateSelector({
          ...selector!,
          ...rest,
          rules: rules.map(rule => {
            const selectorRule = selector?.rules.find(r => r.id === rule.id);
            return {
              ...selectorRule,
              attributeType: rule.attributeType,
              comparisonOperator: rule.comparisonOperator,
              value: rule.value,
              valueDataType: rule.valueDataType,
              changeLog: selectorRule?.changeLog
            };
          })
        });
      }

      // Reset form and navigate to previous page
      onDismiss();
      actions.reset(values);
    } catch (error) {
      setError(error);
    }
  };

  return (
    <Stack
      verticalFill
      styles={{
        root: { padding: theme.spacing.s1, width: "100%" }
      }}
      tokens={{ childrenGap: theme.spacing.m }}
    >
      <SectionTitle>{modeLabel} Selector</SectionTitle>
      <SelectorForm
        onSubmit={onSubmit}
        onDismiss={onDismiss}
        selector={selector}
        error={error}
      />
    </Stack>
  );
};
